import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import { isEmpty } from 'lodash'
import { useQuery } from '@tanstack/react-query'
import SelectWithCheckbox from '../../molecules/Select/SelectWithCheckbox'
import Skeleton from '../../atoms/Skeleton'
import { useBoolean, useSearchAssetsDebounced } from '../../../hooks'
import SelectWithCheckboxAndSearch from '../../molecules/Select/SelectWithCheckboxAndSearch'
import { BUTTON_SIZES } from '../../../constants'
import useSearchTransactionTagsDebounced from '../../../hooks/useSearchTransactionTagsDebounced'
import { useAppContext } from '../../../redux/slices/appContext'
import { postNamedQuery } from '../../../service'
import { useTransactionsContext } from './TransactionsProvider'

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.layout.gap.g5
  }
}))

const mapDefaultSelectedOptions = (defaultValues, options) => {
  if (isEmpty(defaultValues)) return []
  const values = defaultValues.map(Number)
  return (options || [])
    .filter(({ value }) => values.includes(value))
    .reduce((acc, option) => ({ ...acc, [option.value]: option }), {})
}

const TransactionsTableFilters = ({
  defaultCustodianValues,
  defaultAssetValues,
  defaultTagValues,
  defaultFilter,
  enabledFilters
}) => {
  const { clientId } = useAppContext()
  const classes = useStyles()
  const [tagSearchMode, setTagSearchMode] = useBoolean()
  const [assetSearchMode, setAssetSearchMode] = useBoolean()
  const { data: custodians, isLoading: loadingCustodians } = useQuery({
    queryKey: ['custodians', clientId],
    queryFn: async () => {
      const result = await postNamedQuery('coreData', 'custodians', {
        filters: {
          clientIds: [clientId]
        }
      })
      return result.data
    },
    select: (data) => data.map((datum) => ({
      key: datum.custodianCode,
      value: datum.custodianId,
      label: datum.custodianName
    })),
    enabled: !!clientId && enabledFilters?.includes('custodians')
  })

  const { setTableFilter } = useTransactionsContext()

  const transactionsQuery = useMemo(() => {
    return {
      query: {
        filters: defaultFilter ?? {}
      },
      defaultTransactionIds: defaultTagValues,
      queryOptions: { enabled: enabledFilters?.includes('tags') }
    }
  }, [defaultFilter, defaultTagValues, enabledFilters])

  const {
    options: transactionTagOptions = [],
    defaultOptions: transactionDefaultOptions = [],
    isLoading: isLoadingTransactionTags,
    onChangeQuery: onChangeTransactionTagsQuery,
    isSearchLoading: isTagSearchLoading
  } = useSearchTransactionTagsDebounced(transactionsQuery)

  const assetsQuery = useMemo(() => {
    return {
      defaultAssetIds: defaultAssetValues,
      query: { filters: { clientId } },
      queryOptions: { enabled: enabledFilters?.includes('assets') }
    }
  }, [defaultAssetValues, clientId, enabledFilters])

  const {
    options: assetOptions = [],
    defaultOptions: assetDefaultOptions = [],
    isLoading: isLoadingAssets,
    onChangeQuery: onChangeAssetsQuery,
    isSearchLoading: isAssetSearchLoading
  } = useSearchAssetsDebounced(assetsQuery)

  const onChange = useCallback(
    (searchParamKey) => (searchParamValue) => {
      const searchParamValues = searchParamValue?.length ? searchParamValue.map(({ value }) => value) : null
      setTableFilter(prev => ({ ...prev || {}, [searchParamKey]: searchParamValues }))
    },
    [setTableFilter]
  )

  const onChangeCustodian = useMemo(() => onChange('custodianId'), [onChange])
  const onChangeTag = useMemo(() => onChange('transactionCodeTagId'), [onChange])
  const onChangeAsset = useMemo(() => onChange('assetId'), [onChange])

  const defaultCustodianOptions = useMemo(() => {
    return mapDefaultSelectedOptions(defaultCustodianValues, custodians)
  }, [defaultCustodianValues, custodians])

  const defaultTagOptions = useMemo(() => {
    const options = [...transactionDefaultOptions, ...transactionTagOptions]
    return mapDefaultSelectedOptions(defaultTagValues, options)
  }, [defaultTagValues, transactionTagOptions, transactionDefaultOptions])

  const defaultAssetOptions = useMemo(() => {
    const options = [...assetDefaultOptions, ...assetOptions]
    return mapDefaultSelectedOptions(defaultAssetValues, options)
  }, [defaultAssetValues, assetOptions, assetDefaultOptions])

  if (!enabledFilters?.length) {
    return null
  }

  return (
    <div className={classes.container}>
      {enabledFilters?.includes('custodians') && (
        loadingCustodians ? (
          <Skeleton height='2rem' width='8rem' />
        ) : (
          <SelectWithCheckbox
            disabled={loadingCustodians}
            placeholder='Custodian'
            prefixLabel='Custodian'
            options={custodians}
            onChange={onChangeCustodian}
            defaultSelectedOptions={defaultCustodianOptions}
            size={BUTTON_SIZES.small}
          />
        ))}

      {enabledFilters?.includes('tags') && (
        !tagSearchMode && isLoadingTransactionTags ? (
          <Skeleton height='2rem' width='8rem' />
        ) : (
          <SelectWithCheckboxAndSearch
            placeholder='Tags'
            searchPlaceholder='Search by tag name'
            options={transactionTagOptions}
            onChange={onChangeTag}
            disabled={isLoadingTransactionTags}
            onClear={onChangeTransactionTagsQuery}
            loading={isTagSearchLoading}
            isLoading={isTagSearchLoading}
            onBlur={setTagSearchMode.off}
            onFocus={setTagSearchMode.on}
            onQueryChange={onChangeTransactionTagsQuery}
            defaultSelectedOptions={defaultTagOptions}
            defaultOptions={transactionDefaultOptions}
            size={BUTTON_SIZES.small}
          />
        ))}

      {enabledFilters?.includes('assets') && (
        !assetSearchMode && isLoadingAssets ? (
          <Skeleton height='2rem' width='8rem' />
        ) : (
          <SelectWithCheckboxAndSearch
            placeholder='Assets'
            searchPlaceholder='Search by symbol'
            options={assetOptions}
            onChange={onChangeAsset}
            disabled={isLoadingAssets}
            onClear={onChangeAssetsQuery}
            loading={isAssetSearchLoading}
            isLoading={isAssetSearchLoading}
            onQueryChange={onChangeAssetsQuery}
            onBlur={setAssetSearchMode.off}
            onFocus={setAssetSearchMode.on}
            defaultSelectedOptions={defaultAssetOptions}
            defaultOptions={assetDefaultOptions}
            size={BUTTON_SIZES.small}
          />
        ))}
    </div>
  )
}

TransactionsTableFilters.propTypes = {
  defaultCustodianValues: PropTypes.object,
  defaultTagValues: PropTypes.object,
  defaultAssetValues: PropTypes.object,
  defaultFilter: PropTypes.object,
  enabledFilters: PropTypes.array
}

TransactionsTableFilters.defaultProps = {
  defaultCustodianValues: {},
  defaultAssetValues: {},
  defaultTagValues: {},
  defaultFilter: {},
  enabledFilters: ['custodians', 'tags', 'assets']
}

export default TransactionsTableFilters
